'use client';

import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { normalizeBaseId } from '../lib/courseIdFormatter';
import { acquirePhysicalWrite, getPhysicalWrite, releasePhysicalWrite, subscribePhysicalWrite, SCHEDULE_WRITE_SCOPE } from '../hooks/physicalWriteSlot';

export interface ScheduleWriteIdentity { ownerId: string; scheduleId: string; routeKey: string; term: string }
export interface ScheduleContextAuthority { identity: ScheduleWriteIdentity; isContextCurrent: () => boolean }
export interface ScheduleWriteOperation extends ScheduleContextAuthority { isLifetimeCurrent: () => boolean; isCurrent: () => boolean; finish: () => void }
interface Options { ownerId: string | null; scheduleId: string | null; routeKey: string; term: string; draft: unknown }

/** The existing retained schedule slot plus a committed context/draft receipt. */
export function useScheduleWriteAuthority({ ownerId, scheduleId, routeKey, term, draft }: Options) {
  const context = useMemo(() => ({ ownerId, scheduleId, routeKey, term }), [ownerId, scheduleId, routeKey, term]);
  const draftIdentity = useMemo(() => ({ draft }), [draft]);
  const committed = useRef<{ context: typeof context; draftIdentity: typeof draftIdentity } | null>(null);
  const lifetime = useRef<symbol | null>(null);
  const [isPending, setIsPending] = useState(() => getPhysicalWrite(SCHEDULE_WRITE_SCOPE) !== undefined);
  useLayoutEffect(() => { committed.current = { context, draftIdentity }; });
  useLayoutEffect(() => {
    const epoch = Symbol('schedule-write-context'); lifetime.current = epoch;
    return () => { if (lifetime.current === epoch) lifetime.current = null; };
  }, [context]);
  useLayoutEffect(() => {
    const update = () => { if (lifetime.current !== null) setIsPending(getPhysicalWrite(SCHEDULE_WRITE_SCOPE) !== undefined); };
    const unsubscribe = subscribePhysicalWrite(SCHEDULE_WRITE_SCOPE, update); update(); return unsubscribe;
  }, []);
  const captureContext = useCallback((): ScheduleContextAuthority | null => {
    const epoch = lifetime.current;
    if (!epoch || committed.current?.context !== context || !context.ownerId || !context.scheduleId) return null;
    const identity: ScheduleWriteIdentity = { ownerId: context.ownerId, scheduleId: context.scheduleId, routeKey: context.routeKey, term: context.term };
    return { identity, isContextCurrent: () => lifetime.current === epoch && committed.current?.context === context };
  }, [context]);
  const begin = useCallback((): ScheduleWriteOperation | null => {
    const authority = captureContext();
    if (!authority || committed.current?.draftIdentity !== draftIdentity) return null;
    const id = Symbol('schedule-write');
    if (!acquirePhysicalWrite(SCHEDULE_WRITE_SCOPE, id)) return null;
    const isContextCurrent = () => authority.isContextCurrent() && getPhysicalWrite(SCHEDULE_WRITE_SCOPE) === id;
    return { identity: authority.identity, isContextCurrent, isLifetimeCurrent: () => authority.isContextCurrent() && committed.current?.draftIdentity === draftIdentity,
      isCurrent: () => isContextCurrent() && committed.current?.draftIdentity === draftIdentity,
      finish: () => releasePhysicalWrite(SCHEDULE_WRITE_SCOPE, id) };
  }, [captureContext, draftIdentity]);
  const isContextCurrent = useCallback(() => lifetime.current !== null && committed.current?.context === context, [context]);
  return { begin, captureContext, isContextCurrent, isPending };
}


/** Only a newer intent for the same course retires its catalog continuation. */
export function useCourseRemovalIntent(contextKey: string, courses: readonly { id: string }[]) {
  const context = useMemo(() => ({ contextKey }), [contextKey]);
  const committed = useRef<typeof context | null>(null);
  const intents = useRef(new Map<string, symbol>());
  const previous = useRef(new Map<string, object>());
  useLayoutEffect(() => {
    committed.current = context; intents.current.clear(); previous.current.clear();
    return () => { if (committed.current === context) { committed.current = null; intents.current.clear(); previous.current.clear(); } };
  }, [context]);
  useLayoutEffect(() => {
    const current = new Map(courses.map(course => [normalizeBaseId(course.id), course]));
    for (const [target, course] of current) {
      if (intents.current.has(target) && previous.current.get(target) !== course) intents.current.set(target, Symbol('new-course-intent'));
    }
    previous.current = current;
  }, [context, courses]);
  const invalidate = useCallback((target: string) => {
    if (committed.current === context && intents.current.has(target)) intents.current.set(target, Symbol('explicit-course-intent'));
  }, [context]);
  const capture = useCallback((target: string): (() => boolean) | null => {
    if (committed.current !== context) return null;
    const token = Symbol('remove-course'); intents.current.set(target, token);
    return () => committed.current === context && intents.current.get(target) === token;
  }, [context]);
  return { capture, invalidate };
}
